import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Compass, FileSearch, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ClinqLogo } from "../components/ClinqLogo";

export const PATHWAYS = {
  direct: { to: "/founder/apply", label: "Apply to a funder" },
  guided: { to: "/onboarding", label: "Capital Readiness Report" },
};

const OPTIONS = [
  {
    key: "direct", Icon: Compass, eyebrow: "Option 1",
    title: "Apply directly to a funder today",
    body: "Best if you already have your documents ready.",
    detail: "See the funders matched to your business, pick the ones that fit, upload your pack and we'll tell you within minutes whether you qualify.",
    cta: "Browse funders",
  },
  {
    key: "guided", Icon: FileSearch, eyebrow: "Option 2",
    title: "Get my Capital Readiness Report and let Clinq guide me",
    body: "Best if you want help preparing first.",
    detail: "We score your business across the six pillars funders look at, show who is most likely to fund you, and your Relationship Manager walks you through what to fix.",
    cta: "Start my assessment",
  },
];

export default function FounderPathway() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState("");
  if (user?.company_kind === "project") return <Navigate to="/project/report" replace />;
  const first = (user?.name || "").split(" ")[0];

  const choose = async (key) => {
    setBusy(key);
    try {
      await api.post("/founder/pathway", { pathway: key });
      await refresh();
      nav(PATHWAYS[key].to, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save your choice — please try again");
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F8F8] px-4 py-10 sm:px-6" style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }} data-testid="founder-pathway">
      <div className="mx-auto max-w-4xl">
        <ClinqLogo className="h-8" />
        <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Welcome{first ? `, ${first}` : ""}</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-[#00303F] sm:text-4xl">How would you like to raise capital?</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base">
          Choose the path that suits you now. You can switch between the two at any time from your dashboard.
        </p>

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {OPTIONS.map(({ key, Icon, eyebrow, title, body, detail, cta }) => (
            <button key={key} type="button" onClick={() => choose(key)} disabled={!!busy}
              className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#0A8B85] hover:shadow-lg disabled:opacity-60"
              data-testid={`pathway-option-${key}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{eyebrow}</span>
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A8B85]/10 text-[#0A8B85]"><Icon size={20} /></span>
              </div>
              <h2 className="mt-4 text-lg font-extrabold leading-snug text-[#00303F]">{title}</h2>
              <p className="mt-2 text-sm font-semibold text-slate-700">“{body}”</p>
              <p className="mt-2 flex-1 text-sm text-slate-500">{detail}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0A8B85]">
                {busy === key ? <Loader2 size={16} className="animate-spin" /> : null}
                {busy === key ? "One moment…" : cta}
                {busy !== key && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
